import React, { useState } from "react";
import { useTranslation } from "next-i18next";
import EWPdpSelector from "./EWPdpSelector";
import EWMoreInfoModal from "./EWMoreInfoModal";
import { groupPricesByCategory, getEWSummaryForLine } from "./ewModel";
import {
  getCartLineSlots,
  getSelectedPriceForSlot,
  useCart,
} from "@/contexts/cart/CartContext";
import {
  UPDATE_EXTENDED_WARRANTY,
  REMOVE_EXTENDED_WARRANTY,
} from "@/services/extended-warranty";
import { GET_CART } from "@/services/cart";
import ImgTag from "../Helpers/ImgTag";
import useEwSlotsScrollCap from "@/hooks/useEwSlotsScrollCap";

/**
 * Renders Extended Warranty UI for a single cart line item.
 *
 * Modes:
 *   mini      — one ExtendedWarranty selector per slot (mini-cart drawer)
 *   cart      — same as mini, plus a per-slot "Al Manea Guarantee" title
 *   readonly  — flat rows of selected slots only (checkout / my-order parity)
 *
 * Props:
 *   item        — cart line item (with extension_attributes.extended_warrantys[])
 *   mode
 *   locale
 *   onSlotChange — optional override(handler({cart_item_id, position, extended_warranty_id, price_id, action: 'change'|'clear'}))
 *                  default: calls UPDATE_EXTENDED_WARRANTY service and triggers refetchCart prop
 *   refetchCart  — callback to refresh cart after a slot write (so the new EW state appears)
 */
const EWSlots = ({
  item,
  mode = "cart",
  locale,
  onSlotChange,
  refetchCart,
}) => {
  const { t } = useTranslation("extended_warranty");
  const [pendingPosition, setPendingPosition] = useState(null);
  // Category tab currently being viewed, per slot (position → category_id).
  // Local UI state only — switching a tab does NOT write to the cart; only
  // picking a duration does. Absent → falls back to the slot's selected category.
  const [viewedCategory, setViewedCategory] = useState({});
  // "more info" popup (BE-returned HTML) — one instance for this line.
  const [moreInfoOpen, setMoreInfoOpen] = useState(false);
  const { dispatch } = useCart();

  // EW slots for this line — computed up-front so the scroll-cap hook below
  // can depend on the count and still run before the early returns.
  const slots = getCartLineSlots(item);

  // EW list scroll cap (shared with the PDP) — desktop shows VISIBLE_EW_SLOTS
  // blocks then scrolls internally; mobile (and ≤ N slots) shows all. Disabled
  // in readonly mode (flat rows, no scroll container).
  const { scrollRef, maxHeight } = useEwSlotsScrollCap(slots.length, {
    enabled: mode !== "readonly",
  });

  // Fallback refetch — call GET_CART and dispatch into reducer.
  // Callers using SWR should pass their own `refetchCart` (e.g. SWR `mutate`)
  // so the SWR cache stays in sync.
  const defaultRefetch = async () => {
    const res = await GET_CART();
    if (res?.data?.data) {
      dispatch({ type: "GET_CART", payload: res.data.data });
    }
  };
  const doRefetch = refetchCart || defaultRefetch;

  // BOGO gate — hide EW on free promo lines
  if (item?.extension_attributes?.amasty_promo?.is_free === true) {
    return null;
  }

  if (!slots.length) return null;

  const performWrite = async (payload, action) => {
    setPendingPosition(payload.position);
    try {
      if (action === "clear") {
        // Clearing still needs a valid extended_warranty_id (update-only
        // endpoint); price_id:null deselects.
        await REMOVE_EXTENDED_WARRANTY({
          cart_item_id: payload.cart_item_id,
          position: payload.position,
          extended_warranty_id: payload.extended_warranty_id,
        });
      } else {
        await UPDATE_EXTENDED_WARRANTY(payload);
      }
      await doRefetch();
    } finally {
      setPendingPosition(null);
    }
  };

  const handleChange = async (slot, price_id) => {
    const payload = {
      cart_item_id: slot.cart_item_id ?? item?.item_id,
      position: slot.position,
      extended_warranty_id: slot.extended_warranty_id,
      price_id,
    };
    if (onSlotChange) return onSlotChange({ ...payload, action: "change" });
    return performWrite(payload, "change");
  };

  const handleClear = async (slot) => {
    const payload = {
      cart_item_id: slot.cart_item_id ?? item?.item_id,
      position: slot.position,
      // Carry the currently-selected warranty id — the BE deselect contract
      // needs a valid extended_warranty_id alongside price_id:null to clear.
      extended_warranty_id: slot.extended_warranty_id,
    };
    if (onSlotChange) return onSlotChange({ ...payload, action: "clear" });
    return performWrite(payload, "clear");
  };

  if (mode === "readonly") {
    const summary = getEWSummaryForLine(item);
    if (summary.length) {
      return (
        <div className="flex flex-col gap-2 my-2">
          {summary.map((row) => (
            <div
              key={`${row.order_item_id ?? row.cart_item_id ?? "ew"}-${row.price_id}`}
              className="flex items-center gap-3 py-2"
              dir={locale === "ar" ? "rtl" : "ltr"}
            >
              <div className="relative shrink-0">
                <div className="flex items-center justify-center w-26 h-24 p-6 overflow-hidden bg-white rounded-md shadow-sm">
                  <ImgTag
                    src={row.logo || "/logo.webp"}
                    alt={row.program || "Almanea"}
                    width={32}
                    height={32}
                    className="object-contain w-full h-full"
                  />
                </div>
                {row.qty ? (
                  <span className="absolute top-1 end-1 min-w-[20px] h-4 px-1.5 rounded-full bg-[#9CA3AF] text-white text-[11px] leading-none flex items-center justify-center">
                    {row.qty}
                  </span>
                ) : null}
              </div>
              <span className="text-base text-[#0C0A09] font-semibold">
                {row.program ||
                  t(`options.${row.code}`, { defaultValue: row.code })}
              </span>
            </div>
          ))}
        </div>
      );
    }

    // Fallback: one row per filled slot. Detect "filled" via prices[].is_selected
    // (works for both cart and order shapes), not the top-level price_id.
    const filled = slots.filter((s) => getSelectedPriceForSlot(s));
    if (!filled.length) return null;
    return (
      <div className="flex flex-col gap-2 my-2">
        {filled.map((slot) => {
          const sel = getSelectedPriceForSlot(slot);
          if (!sel) return null;
          return (
            <div
              key={`${slot.order_item_id ?? slot.cart_item_id ?? "ew"}-${slot.position}`}
              className="flex items-center gap-3 py-2"
              dir={locale === "ar" ? "rtl" : "ltr"}
            >
              <div className="relative shrink-0">
                <div className="flex items-center justify-center w-26 h-24 p-6 overflow-hidden bg-white rounded-md shadow-sm">
                  <ImgTag
                    src={sel.logo || "/logo.webp"}
                    alt={sel.program || "Almanea"}
                    width={32}
                    height={32}
                    className="object-contain w-full h-full"
                  />
                </div>
                {slot?.qty ? (
                  <span className="absolute top-1 end-1 min-w-[20px] h-4 px-1.5 rounded-full bg-[#9CA3AF] text-white text-[11px] leading-none flex items-center justify-center">
                    {slot.qty}
                  </span>
                ) : null}
              </div>
              <span className="text-base text-[#0C0A09] font-semibold">
                {sel.program ||
                  t(`options.${sel.code}`, { defaultValue: sel.code })}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  const isMini = mode === "mini";

  return (
    <div
      ref={scrollRef}
      className={`flex flex-col gap-2 ${
        maxHeight != null ? "overflow-y-auto pe-2" : ""
      }`}
      style={maxHeight != null ? { maxHeight: `${maxHeight}px` } : undefined}
    >
      {slots.map((slot) => {
        const selectedPriceId = slot?.price_id ?? null;
        const categories = groupPricesByCategory(
          slot.prices,
          slot.program_groups
        );
        // Highlight the selected duration's category; null when nothing is
        // selected (don't auto-highlight the first group).
        const selectedCategoryId =
          categories.find((c) =>
            c.durations.some((d) => d.price_id === selectedPriceId)
          )?.category_id ?? null;
        // The tab the user is viewing — selected category until they tap another.
        const viewedCategoryId =
          viewedCategory[slot.position] ?? selectedCategoryId;
        return (
          <div key={slot.position} className="w-full">
            {mode === "cart" && (
              <div
                className="flex items-center gap-2 px-1 pt-2"
                dir={locale === "ar" ? "rtl" : "ltr"}
              >
                <span className="text-[14px] text-[#00269A] font-[500]">
                  {t("forProduct", {
                    defaultValue: "Al Manea Guarantee (For product {{n}})",
                    n: slot.position + 1,
                  })}
                </span>
              </div>
            )}
            <EWPdpSelector
              categories={categories}
              configuration={slot?.configuration}
              selectedCategoryId={viewedCategoryId}
              // The selected duration only highlights while its own category is
              // the one being viewed (tab-switching shows an unselected category).
              selectedPriceId={selectedPriceId}
              onCategoryChange={(categoryId) =>
                setViewedCategory((prev) => ({
                  ...prev,
                  [slot.position]: categoryId,
                }))
              }
              onDurationChange={(priceId) => handleChange(slot, priceId)}
              onDurationClear={() => handleClear(slot)}
              onMoreInfo={() => setMoreInfoOpen(true)}
              locale={locale}
              compact={isMini}
              isPending={pendingPosition === slot.position}
            />
          </div>
        );
      })}
      <EWMoreInfoModal
        isOpen={moreInfoOpen}
        onClose={() => setMoreInfoOpen(false)}
        locale={locale}
      />
    </div>
  );
};

export default EWSlots;
